import { useMemo, useState } from "react";
import { Button } from "@humansignal/ui";
import { useAPI } from "../../../providers/ApiProvider";
import { projectHasAudioRecord } from "../AudioQCSettings";
import { useTranslation } from "../../../i18n/LocaleProvider";

/**
 * One-click "Add Audio QC pipeline" banner that shows up on the
 * project's Models settings page when:
 *
 *   1. The label_config contains an <AudioRecord> tag, AND
 *   2. The project has no MLBackend registered with
 *      ``model_version='audio-qc'`` yet.
 *
 * Clicking POSTs an MLBackend pointed at the in-process audio_qc
 * service (no external URL — the backend's signal handler runs
 * cheap checks inline). For heavy GPU checks the operator changes
 * ``audio_qc_dispatch_mode = 'mlbackend'`` and registers a separate
 * external service the standard way; we don't replace that flow,
 * just speed up the common case.
 */
export const AudioQCSuggestion = ({ project, backends, onAdded }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const [working, setWorking] = useState(false);

  const isVoiceProject = useMemo(() => projectHasAudioRecord(project), [project]);
  const hasAudioQc = useMemo(
    () => (backends || []).some((b) => (b.model_version || "").toLowerCase() === "audio-qc"),
    [backends],
  );

  if (!isVoiceProject || hasAudioQc) return null;

  const onAdd = async () => {
    setWorking(true);
    // We register the in-process pipeline as a placeholder MLBackend
    // pointing at this Django itself. The signal handler in
    // ``audio_qc/`` short-circuits dispatch when
    // ``Project.audio_qc_dispatch_mode === 'in_process'``, so the URL
    // is never actually called — it just satisfies LS's "every model
    // has a URL" invariant. Operators flip to MLBackend dispatch on
    // the Audio QC tab and re-point this row when they're ready for
    // GPU-heavy checks.
    const url =
      typeof window !== "undefined"
        ? `${window.location.protocol}//${window.location.host}/api/audio-qc/`
        : "/api/audio-qc/";
    const res = await api.callApi("addMLBackend", {
      body: {
        project: project.id,
        url,
        title: "Audio QC pipeline",
        description:
          "Automated quality checks (peak / clipping / SNR / loudness / VAD / " +
          "speech-ratio / silence) for <AudioRecord> submissions. Runs in-" +
          "process by default; switch to external in Audio QC settings for " +
          "heavy checks.",
        model_version: "audio-qc",
        is_interactive: false,
        auto_update: false,
      },
    });
    setWorking(false);
    if (res && !res.error) onAdded?.();
  };

  return (
    <div
      role="region"
      aria-label={t("misc.audio_qc_suggestion")}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "12px 14px",
        marginBottom: 14,
        border: "1px solid var(--color-primary-border-subtle, rgba(192, 53, 58, 0.3))",
        background: "rgba(192, 53, 58, 0.04)",
        borderRadius: 8,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "var(--font-size-body-small)", fontWeight: 600, marginBottom: 2 }}>
          Add the Audio QC pipeline
        </div>
        <div
          style={{
            fontSize: "var(--font-size-body-small)",
            color: "var(--color-neutral-content-subtler)",
            lineHeight: 1.4,
          }}
        >
          This project uses <code>&lt;AudioRecord&gt;</code>. Connect the QC pipeline so every recording is
          automatically scored on signal quality and routed to review when something looks off. Opens the Audio QC tab
          to tune thresholds.
        </div>
      </div>
      <Button variant="primary" look="filled" size="small" disabled={working} onClick={onAdd}>
        {working ? "Adding…" : "Add Audio QC"}
      </Button>
    </div>
  );
};
